import {classToPlain, Exclude, Expose, plainToClass} from "class-transformer";
import * as t from "io-ts";
import "reflect-metadata";
import {v4 as uuid} from "uuid";
import {validateJSON} from "../web/validation";
import type {SystemAlertType} from "./alert";
import {SystemAlertBaseValidator, SystemAlertEntity, systemAlertType} from "./alert";
import {CargoSubtype, toCargoSubtype} from "./cargo-subtype";
import {ContainerEntity} from "./container";
import {ContainerAlertBuilder, ContainerBuilderStep} from "./container-alert-builder";
import {DatabaseCode, toDatabaseCode} from "./database-code";

/** Validator for a container alert type. */
export const ContainerAlertValidator = t.intersection([
  SystemAlertBaseValidator,
  t.type({
    id: t.string,
    containerId: t.string,
    regionId: t.string,
    alertType: t.string,
    alertDttm: t.number,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    alertValue: t.number,
    alertUom: t.string,
    cargoSubtype: t.string,
    dispatchRequestId: t.string,
    dispatchId: t.string,
    comment: t.string,
    createdById: t.string,
    triggerObjectId: t.string,
    triggerObjectType: t.string,
    database: t.string,
  }),
]);

export const ContainerAlertArrayValidator = t.array(ContainerAlertValidator);

export type ContainerAlert = t.TypeOf<typeof ContainerAlertValidator>;
export type ContainerAlertArray = t.TypeOf<typeof ContainerAlertArrayValidator>;

/**
 * The type of container level.
 */
export interface ContainerAlertType extends DatabaseCode {
}

const normal: ContainerAlertType = {
  code: 'Normal',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.normal',
    defaultMessage: 'Normal',
  },
};

const lowLevel: ContainerAlertType = {
  code: 'Low Level',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.low',
    defaultMessage: 'Low Level',
  },
};

const lowLevelCritical: ContainerAlertType = {
  code: 'Low Critial',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.lowcritical',
    defaultMessage: 'Critical Low Level',
  },
};

const internDecrease: ContainerAlertType = {
  code: 'Intern Decrease',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.interndecrease',
    defaultMessage: 'Intermediate Decrease',
  },
};

const internIncrease: ContainerAlertType = {
  code: 'Intern Increase',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.internincrease',
    defaultMessage: 'Intermediate Increase',
  },
};

const highLevel: ContainerAlertType = {
  code: 'High Level',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.high',
    defaultMessage: 'High Level',
  },
};

const highLevelCritical: ContainerAlertType = {
  code: 'High Critical',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.highcritical',
    defaultMessage: 'Critical High Level',
  },
};

const scadaSilence: ContainerAlertType = {
  code: 'Scada Silence',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.scadasilience',
    defaultMessage: 'No Communication',
  },
};

const sticking: ContainerAlertType = {
  code: 'Sticking',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.sticking',
    defaultMessage: 'Sticking',
  },
};

const minimumIncrease: ContainerAlertType = {
  code: 'Minimum Increase',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.minimumincrease',
    defaultMessage: 'Minimum Increase Not Achieved',
  },
};

const minimumDecrease: ContainerAlertType = {
  code: 'Minimum Decrease',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.minimumdecrease',
    defaultMessage: 'Minimum Decrease Not Achieved',
  },
};

const badScada: ContainerAlertType = {
  code: 'Bad Scada',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.badscada',
    defaultMessage: 'Bad Scada',
  },
};

const unchangedLevel: ContainerAlertType = {
  code: 'Unchanged Level',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.unchangedlevel',
    defaultMessage: 'Unchanged Level',
  },
};

const oilPull: ContainerAlertType = {
  code: 'Oil Pull',
  displayValue: {
    id: 'ots.ui.entity.container.alert.type.oilpull',
    defaultMessage: 'Oil Pull',
  },
};

/**
 * Alert types for {@link ContainerAlert}.
 */
export const containerAlertTypes = {
  lowLevel,
  lowLevelCritical,
  internDecrease,
  internIncrease,
  highLevel,
  highLevelCritical,
  normal,
  scadaSilence,
  sticking,
  minimumIncrease,
  minimumDecrease,
  badScada,
  unchangedLevel,
  oilPull,
};

/**
 * Return ContainerType by code value.
 *
 * @param code - Code value
 * @returns {ContainerAlertType} The matching ContainerType
 */
export const toContainerAlertType = (code: string) => toDatabaseCode(code, Object.values(containerAlertTypes));

/**
 * A container alert.
 */
@Exclude()
export class ContainerAlertEntity implements SystemAlertEntity {
  @Expose() id!: string;
  @Expose() containerId!: string;
  @Expose() regionId!: string;
  @Expose({name: 'alertType'}) private _alertType!: string;
  @Expose() alertValue?: number;
  @Expose() alertUom?: string;
  @Expose() alertDttm!: number;
  @Expose({name: 'cargoSubtype'}) private _cargoSubtype?: string;
  @Expose() dispatchRequestId?: string;
  @Expose() dispatchId?: string;
  @Expose() comment?: string;
  @Expose() createdById?: string;
  @Expose() triggerObjectId?: string;
  @Expose() triggerObjectType?: string;
  @Expose() database?: string;
  @Expose() private createDttm!: number;
  @Expose() private lastModifiedDttm!: number;

  // for system alert
  @Expose() private systemAlertDttm?: number;

  /**
   * Constructor for ContainerAlertEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static builder(id: string = uuid()): ContainerBuilderStep {
    return new ContainerAlertBuilder(id);
  }

  /**
   * Create a ContainerAlertEntity.
   *
   * @param builder the container alert builder
   */
  static create(builder: ContainerAlertBuilder): ContainerAlertEntity {
    const entity = new ContainerAlertEntity();
    entity.id = builder.id;
    entity.containerId = builder.containerId;
    entity.regionId = builder.regionId;
    entity.alertType = builder.alertType;
    entity.alertValue = builder.alertValue;
    entity.alertUom = builder.alertUom;
    entity.alertDate = builder.alertDate;
    entity.cargoSubtype = builder.cargoSubtype;
    entity.dispatchRequestId = builder.dispatchRequestId;
    entity.dispatchId = builder.dispatchId;
    entity.comment = builder.comment;
    entity.createdById = builder.createdById;
    entity.triggerObjectId = builder.triggerObjectId;
    entity.triggerObjectType = builder.triggerObjectType;
    entity.createDate = builder.createDate;
    entity.lastModifiedDate = builder.lastModifiedDate;
    return entity;
  }

  /**
   * Create a ContainerAlertEntity from a ContainerAlert type value.
   *
   * @param containerAlert The container alert type value.
   */
  static from(containerAlert: ContainerAlert): ContainerAlertEntity {
    const entity: ContainerAlertEntity = plainToClass(
      ContainerAlertEntity,
      containerAlert,
      {
        excludeExtraneousValues: true,
      }
    );
    return entity;
  }

  /**
   * Deserializes the ContainerAlertEntity to a ContainerAlert type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<ContainerAlert> {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ContainerAlertValidator, object);
  }

  get cargoSubtype(): CargoSubtype | undefined {
    return this._cargoSubtype ? toCargoSubtype(this._cargoSubtype) : undefined;
  }

  set cargoSubtype(type: CargoSubtype | undefined) {
    this._cargoSubtype = type?.code;
  }

  get alertType(): ContainerAlertType {
    return toContainerAlertType(this._alertType);
  }

  set alertType(type: ContainerAlertType) {
    this._alertType = type.code;
  }

  get alertDate(): Date {
    return new Date(this.alertDttm);
  }

  set alertDate(date: Date) {
    this.alertDttm = date.getTime();
  }

  get createDate(): Date {
    return new Date(this.createDttm);
  }

  set createDate(date: Date) {
    this.createDttm = date.getTime();
  }

  get lastModifiedDate(): Date {
    return new Date(this.lastModifiedDttm);
  }

  set lastModifiedDate(date: Date) {
    this.lastModifiedDttm = date.getTime();
  }

  set container(container: ContainerEntity) {
    this.containerId = container.id;
  }

  getSystemAlertId = (): string => {
    return this.id;
  };

  getSystemAlertType = (): SystemAlertType => {
    return systemAlertType.containerAlert;
  };

  getSystemAlertDate = (): Date | undefined => {
    return this.systemAlertDttm ? new Date(this.systemAlertDttm) : undefined;
  };
}
